import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Badge } from "@/components/ui/badge";
import { CurrencyInput } from "@/components/ui/currency-input";
import { CalendarIcon, Plus, X, Clock } from "lucide-react";
import { cn, formatDateForInput, formatDateOnlyPtBR, parseDateFromInput } from "@/lib/utils";
import type { FieldDefinition, MultiValueEntry, AddressValue, TimeRangeValue, ListItem } from "@/types/fieldRegistry";
import { PHONE_LABELS, EMAIL_LABELS } from "@/types/fieldRegistry";
import { useOrganizationUsers } from "@/hooks/admin/useOrganizationUsers";
import { AttachmentFieldInput } from "@/components/shared/fields/AttachmentFieldInput";
import { MultiValueRenderer } from "@/components/shared/fields/MultiValueRenderer";
import { formatDocument, cleanDocument, documentWarning, DOCUMENT_MAX_LENGTH, type DocumentFieldType } from "@/lib/document-mask";
import { normalizeEstado, opcoesEstado } from "@/lib/estado-br";

interface FieldRendererProps {
  field: FieldDefinition;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
}

export function FieldRenderer({ field, value, onChange, disabled }: FieldRendererProps) {
  switch (field.field_type) {
    case 'text':
    case 'autocomplete':
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.field_placeholder || `Digite ${field.field_label.toLowerCase()}`}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          placeholder={field.field_placeholder || `Digite ${field.field_label.toLowerCase()}`}
          disabled={disabled}
        />
      );

    case 'textarea':
      return (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.field_placeholder || `Digite ${field.field_label.toLowerCase()}`}
          rows={3}
          disabled={disabled}
        />
      );

    case 'monetary':
      return (
        <CurrencyInput
          value={value}
          onValueChange={(val) => onChange(val ?? null)}
          placeholder={field.field_placeholder || "R$ 0,00"}
          disabled={disabled}
        />
      );

    case 'date':
      return <DateFieldRenderer value={value} onChange={onChange} field={field} disabled={disabled} />;

    case 'time':
      return (
        <div className="relative">
          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="time"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="pl-10"
            disabled={disabled}
          />
        </div>
      );

    case 'time_range':
      return <TimeRangeRenderer value={value} onChange={onChange} disabled={disabled} />;

    case 'select':
      return (
        <SearchableSelect
          value={value || ''}
          onValueChange={onChange}
          disabled={disabled}
          placeholder={field.field_placeholder || `Selecione ${field.field_label.toLowerCase()}`}
          options={(field.field_options || []).map(option => ({ value: option, label: option }))}
        />
      );

    case 'multi_select':
      return (
        <MultiSelectRenderer
          value={value}
          onChange={onChange}
          options={field.field_options || []}
          placeholder={field.field_placeholder || 'Selecione opções'}
          disabled={disabled}
        />
      );

    case 'phone':
      return (
        <MultiValueRenderer
          value={value}
          onChange={onChange}
          labels={field.value_labels || PHONE_LABELS}
          inputType="tel"
          placeholder="Telefone"
          disabled={disabled}
        />
      );

    case 'email_field':
      return (
        <MultiValueRenderer
          value={value}
          onChange={onChange}
          labels={field.value_labels || EMAIL_LABELS}
          inputType="email"
          placeholder="E-mail"
          disabled={disabled}
        />
      );

    case 'address':
      return <AddressRenderer value={value} onChange={onChange} disabled={disabled} />;

    case 'user':
      return <UserFieldRenderer value={value} onChange={onChange} field={field} disabled={disabled} />;

    case 'entity_reference':
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`ID ${field.field_label.toLowerCase()}`}
          disabled={disabled}
        />
      );

    case 'labels':
      return <LabelsRenderer value={value} onChange={onChange} disabled={disabled} />;

    case 'list':
      return <ListRenderer value={value} onChange={onChange} disabled={disabled} />;

    case 'attachment':
      return <AttachmentFieldInput value={value} onChange={onChange} disabled={disabled} />;

    case 'estado':
      // Select tolerante: oferece os 27 oficiais, mas se o valor guardado estiver
      // fora da lista ele entra como opção extra em vez de sumir. Um select puro
      // faria 81 registros parecerem vazios — e salvar apagaria o dado.
      return (
        <SearchableSelect
          value={normalizeEstado(value)}
          onValueChange={onChange}
          disabled={disabled}
          placeholder={field.field_placeholder || 'Selecione o estado'}
          options={opcoesEstado(value)}
        />
      );

    case 'cpf':
    case 'cep':
    case 'rg':
      return (
        <DocumentFieldRenderer
          type={field.field_type as DocumentFieldType}
          value={value}
          onChange={onChange}
          field={field}
          disabled={disabled}
        />
      );

    default:
      return (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.field_placeholder || ''}
          disabled={disabled}
        />
      );
  }
}

// ============================================================================
// Sub-componentes
// ============================================================================

/**
 * CPF, CEP e RG. Exibe com máscara, grava só os dígitos como string.
 *
 * Grava limpo de propósito: o campo `cep` era field_type 'number' e o
 * `Number()` do input comia o zero à esquerda (01310-100 → 1310100, sem volta).
 * E a base já tem 1.694 CPFs em dígitos puros contra 26 pontuados — gravar
 * mascarado criaria um terceiro formato em vez de convergir.
 *
 * O aviso de CPF inválido NÃO bloqueia: existem 8 registros antigos com dígito
 * errado, e travar o salvamento impediria editar qualquer outro campo deles.
 */
function DocumentFieldRenderer({
  type,
  value,
  onChange,
  field,
  disabled,
}: {
  type: DocumentFieldType;
  value: any;
  onChange: (v: any) => void;
  field: FieldDefinition;
  disabled?: boolean;
}) {
  // `value` pode chegar como number nos registros gravados antes desta mudança.
  const aviso = documentWarning(type, value);
  const placeholders: Record<DocumentFieldType, string> = {
    cpf: '000.000.000-00',
    cep: '00000-000',
    rg: 'Número do RG',
  };

  return (
    <div className="space-y-1">
      <Input
        value={formatDocument(type, value)}
        onChange={(e) => onChange(cleanDocument(type, e.target.value) || null)}
        placeholder={field.field_placeholder || placeholders[type]}
        disabled={disabled}
        inputMode={type === 'rg' ? 'text' : 'numeric'}
        maxLength={DOCUMENT_MAX_LENGTH[type]}
        aria-invalid={!!aviso}
      />
      {aviso && <p className={cn("text-xs", type === "cpf" ? "text-warning-700" : "text-info")}>{aviso}</p>}
    </div>
  );
}

function DateFieldRenderer({
  value,
  onChange,
  field,
  disabled,
}: {
  value: any;
  onChange: (v: any) => void;
  field: FieldDefinition;
  disabled?: boolean;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? (
            formatDateOnlyPtBR(value)
          ) : (
            <span>{field.field_placeholder || "Selecione a data"}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ? parseDateFromInput(value) : undefined}
          onSelect={(date) => {
            onChange(date ? formatDateForInput(date) : '');
          }}
          initialFocus
          className="p-3 pointer-events-auto"
        />
      </PopoverContent>
    </Popover>
  );
}

function TimeRangeRenderer({
  value,
  onChange,
  disabled,
}: {
  value: TimeRangeValue | null;
  onChange: (v: TimeRangeValue) => void;
  disabled?: boolean;
}) {
  const current = value || { start: '', end: '' };
  return (
    <div className="flex items-center gap-2">
      <Input
        type="time"
        value={current.start}
        onChange={(e) => onChange({ ...current, start: e.target.value })}
        disabled={disabled}
        className="flex-1"
      />
      <span className="text-muted-foreground text-sm">até</span>
      <Input
        type="time"
        value={current.end}
        onChange={(e) => onChange({ ...current, end: e.target.value })}
        disabled={disabled}
        className="flex-1"
      />
    </div>
  );
}

function MultiSelectRenderer({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string[] | null;
  onChange: (v: string[]) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}) {
  const selected = Array.isArray(value) ? value : [];

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 min-h-[36px] p-2 border rounded-md bg-background">
        {selected.length === 0 && (
          <span className="text-muted-foreground text-sm">{placeholder}</span>
        )}
        {selected.map((item) => (
          <Badge key={item} variant="secondary" className="gap-1">
            {item}
            {!disabled && (
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => toggleOption(item)}
              />
            )}
          </Badge>
        ))}
      </div>
      {!disabled && (
        <div className="flex flex-wrap gap-1">
          {options
            .filter((opt) => !selected.includes(opt))
            .map((option) => (
              <Badge
                key={option}
                variant="outline"
                className="cursor-pointer hover:bg-accent"
                onClick={() => toggleOption(option)}
              >
                <Plus className="h-3 w-3 mr-1" />
                {option}
              </Badge>
            ))}
        </div>
      )}
    </div>
  );
}

function AddressRenderer({
  value,
  onChange,
  disabled,
}: {
  value: AddressValue | null;
  onChange: (v: AddressValue) => void;
  disabled?: boolean;
}) {
  const addr = value || {};

  const update = (key: keyof AddressValue, val: string) => {
    onChange({ ...addr, [key]: val });
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="col-span-2">
        <Input
          value={addr.street || ''}
          onChange={(e) => update('street', e.target.value)}
          placeholder="Rua / Endereço"
          disabled={disabled}
        />
      </div>
      <Input
        value={addr.city || ''}
        onChange={(e) => update('city', e.target.value)}
        placeholder="Cidade"
        disabled={disabled}
      />
      <Input
        value={addr.state || ''}
        onChange={(e) => update('state', e.target.value)}
        placeholder="Estado"
        disabled={disabled}
      />
      <Input
        value={addr.zip || ''}
        onChange={(e) => update('zip', e.target.value)}
        placeholder="CEP"
        disabled={disabled}
      />
      <Input
        value={addr.country || ''}
        onChange={(e) => update('country', e.target.value)}
        placeholder="País"
        disabled={disabled}
      />
    </div>
  );
}

function UserFieldRenderer({
  value,
  onChange,
  field,
  disabled,
}: {
  value: any;
  onChange: (v: any) => void;
  field: FieldDefinition;
  disabled?: boolean;
}) {
  const { data: orgUsers } = useOrganizationUsers();
  const options = (orgUsers || []).map((u) => ({
    value: u.user_id,
    label: u.full_name || u.email,
  }));

  return (
    <SearchableSelect
      value={value || ''}
      onValueChange={onChange}
      disabled={disabled}
      placeholder={field.field_placeholder || `Selecione ${field.field_label.toLowerCase()}`}
      options={options}
    />
  );
}

function LabelsRenderer({
  value,
  onChange,
  disabled,
}: {
  value: string[] | null;
  onChange: (v: string[]) => void;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState('');
  const labels = Array.isArray(value) ? value : [];

  const addLabel = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !labels.includes(trimmed)) {
      onChange([...labels, trimmed]);
      setInputValue('');
    }
  };

  const removeLabel = (label: string) => {
    onChange(labels.filter((l) => l !== label));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1 min-h-[32px]">
        {labels.map((label) => (
          <Badge key={label} variant="secondary" className="gap-1">
            {label}
            {!disabled && (
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeLabel(label)} />
            )}
          </Badge>
        ))}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Nova etiqueta"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addLabel();
              }
            }}
            className="flex-1"
          />
          <Button type="button" variant="outline" size="sm" onClick={addLabel}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function ListRenderer({
  value,
  onChange,
  disabled,
}: {
  value: ListItem[] | null;
  onChange: (v: ListItem[]) => void;
  disabled?: boolean;
}) {
  const items: ListItem[] = Array.isArray(value) ? value : [];

  const addItem = () => {
    onChange([...items, { key: '', value: '' }]);
  };

  const updateItem = (index: number, updates: Partial<ListItem>) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            value={item.key}
            onChange={(e) => updateItem(index, { key: e.target.value })}
            placeholder="Rótulo"
            disabled={disabled}
            className="w-[40%]"
          />
          <Input
            value={item.value}
            onChange={(e) => updateItem(index, { value: e.target.value })}
            placeholder="Valor"
            disabled={disabled}
            className="flex-1"
          />
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => removeItem(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button type="button" variant="outline" size="sm" onClick={addItem} className="w-full">
          <Plus className="h-4 w-4 mr-1" /> Adicionar item
        </Button>
      )}
    </div>
  );
}
